import { Grade } from '../types/grade'
import { GQLBugTracker } from '../types/bug-trackers'
import { buildLibraryCacheID } from '../../../sca/libraries/api/graphql/library-row-query'
import {
  Library,
  LibraryApplication,
  LibraryPolicies,
  Vulnerability
} from '../types/library'

// Each library must have a unique hash
const baseMockLibraries: {
  readonly [key: string]: {
    readonly name: string
    readonly language: string
    readonly grade: Grade
    readonly usage?: {
      readonly total: number
      readonly totalUsed: number
    }
    readonly group?: string
    readonly version?: string
    readonly releaseDate?: string
    readonly latestVersion?: string
    readonly latestReleaseDate?: string
    readonly licenses?: string[]
    readonly tags?: string[]
    readonly violatesVersionPolicy?: boolean
    readonly bugTrackers?: GQLBugTracker[]
    readonly ossEnabled?: boolean
    readonly applications?: LibraryApplication[]
    readonly vulnerabilities?: Vulnerability[]
    readonly policies?: LibraryPolicies
  }
} = {
  '356A192B7913B04C54574D18C28D46E6395428AB': {
    name: 'spring-aop-3.2.4.release.jar',
    language: 'Java',
    grade: {
      value: 'F',
      score: 0
    },
    usage: {
      totalUsed: 0,
      total: 0
    },
    version: '3.2.4',
    latestVersion: '4.0.0',
    licenses: ['Apache 2.0'],
    tags: ['TODO', 'Update Me'],
    policies: {
      restrictedByOrg: true,
      invalidVersion: false,
      versionRestriction: null
    },
    applications: [
      {
        id: '1',
        name: 'WebGoat',
        appId: '1',
        appLibraryStatus: 'REPORTED'
      },
      {
        id: '2',
        name: 'puzzlemall',
        appId: '2',
        appLibraryStatus: 'REPORTED'
      },
      {
        id: '3',
        name: 'bodgeit',
        appId: '3',
        appLibraryStatus: 'REPORTED'
      },
      {
        id: '4',
        name: 'boop',
        appId: '4',
        appLibraryStatus: 'REPORTED'
      },
      {
        id: '5',
        name: 'boopy doop',
        appId: '5',
        appLibraryStatus: 'REPORTED'
      },
      {
        id: '6',
        name: 'blop',
        appId: '6',
        appLibraryStatus: 'REPORTED'
      },
      {
        id: '7',
        name: 'table and chair',
        appId: '7',
        appLibraryStatus: 'REPORTED'
      }
    ],
    vulnerabilities: [
      {
        name: 'CVE-2012-1007',
        description:
          'Multiple cross-site scripting (XSS) vulnerabilities in Apache Struts 1.3.10 allow remote attackers to inject arbitrary web script or HTML via (1) the name parameter to struts-examples/upload/upload-submit.do, or the message parameter to (2) struts-cookbook/processSimple.do or (3) struts-cookbook/processDyna.do.',
        visible: true,
        references: [],
        severityCode: 'MEDIUM',
        severityValue: 4.3,
        confidentialityImpact: 'None',
        integrityImpact: 'Partial',
        availabilityImpact: 'None',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Medium',
        hasCve: true,
        hasCvss3: false
      },
      {
        name: 'Invisible Vulnerability',
        description:
          'The MultiPageValidator implementation in Apache Struts 1 1.1 through 1.3.10 allows remote attackers to bypass intended access restrictions via a modified page parameter.',
        visible: false,
        references: [],
        severityCode: 'MEDIUM',
        severityValue: 5,
        confidentialityImpact: 'None',
        integrityImpact: 'Partial',
        availabilityImpact: 'None',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Low',
        hasCve: true,
        hasCvss3: false
      },
      {
        name: 'Vuln with References',
        description:
          'ParametersInterceptor in Apache Struts before 2.3.16.2 does not properly restrict access to the getClass method, which allows remote attackers to "manipulate" the ClassLoader and execute arbitrary code via a crafted request.  NOTE: this vulnerability exists because of an incomplete fix for CVE-2014-0094.',
        visible: true,
        references: [
          { href: 'https://contrastsecurity.com' },
          { href: 'https://csrc.nist.gov/' }
        ],
        severityCode: 'HIGH',
        severityValue: 7.5,
        confidentialityImpact: 'Partial',
        integrityImpact: 'Partial',
        availabilityImpact: 'Partial',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Low',
        hasCve: true,
        hasCvss3: false
      }
    ]
  },
  '356A192B7913B04C54574D18C28D46E6395428AG': {
    name: 'One Bug Tracker',
    language: 'Java',
    grade: {
      value: 'F',
      score: 0
    },
    version: '3.2.4',
    latestVersion: '4.0.0',
    licenses: ['Apache 2.0'],
    bugTrackers: [
      {
        name: 'Bug Tracker A',
        type: 'JIRA',
        creation: new Date(0).getMilliseconds().toString(),
        ticketKey: 'Key-A',
        ticketTitle: 'Some Crazy Bug',
        ticketUrl: '/some-crazy-bug-url'
      }
    ],
    policies: {
      restrictedByOrg: false,
      invalidVersion: true,
      versionRestriction: 0
    },
    vulnerabilities: [
      {
        name: 'CVE-2016-3674',
        description:
          'Multiple XML external entity (XXE) vulnerabilities in the (1) Dom4JDriver, (2) DomDriver, (3) JDomDriver, (4) JDom2Driver, (5) SjsxpDriver, (6) StandardStaxDriver, and (7) WstxDriver drivers in XStream before 1.4.9 allow remote attackers to read arbitrary files via a crafted XML document.',
        visible: true,
        references: [],
        severityCode: 'LOW',
        severityValue: 5,
        confidentialityImpact: 'Partial',
        integrityImpact: 'None',
        availabilityImpact: 'None',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Low',
        hasCve: true,
        hasCvss3: false
      }
    ]
  },
  DA4B9237BACCCDF19C0760CAB7AEC4A8359010B0: {
    name: 'Multiple Bug Trackers',
    language: 'Java',
    grade: {
      value: 'B',
      score: 80
    },
    usage: {
      totalUsed: 0,
      total: 1
    },
    tags: ['Bugz'],
    bugTrackers: [
      {
        name: 'Bug Tracker A',
        type: 'JIRA',
        creation: new Date(0).getMilliseconds().toString(),
        ticketKey: 'Key-A',
        ticketTitle: 'Some Crazy Bug',
        ticketUrl: '/some-crazy-bug-url'
      },
      {
        name: 'Bug Tracker B',
        type: 'VSTS',
        creation: new Date(0).getMilliseconds().toString(),
        ticketKey: 'Key-B',
        ticketTitle: 'Some Crazy Feature Request',
        ticketUrl: '/some-crazy-feature-request-url'
      }
    ],
    policies: {
      restrictedByOrg: false,
      invalidVersion: true,
      versionRestriction: 1
    },
    vulnerabilities: [
      {
        name: 'CVE-2017-5929',
        description:
          'QOS.ch Logback before 1.2.0 has a serialization vulnerability affecting the SocketServer and ServerSocketReceiver components.',
        visible: true,
        references: [],
        severityCode: 'HIGH',
        severityValue: 7.5,
        confidentialityImpact: 'Partial',
        integrityImpact: 'Partial',
        availabilityImpact: 'Partial',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Low',
        hasCve: true,
        hasCvss3: false
      }
    ]
  },
  '77DE68DAECD823BABBB58EDB1C8E14D7106E83BY': {
    name: 'Bug Tracker w/o Ticket URL',
    language: 'Java',
    grade: {
      value: 'UNKNOWN',
      score: -1
    },
    usage: {
      totalUsed: 25,
      total: 100
    },
    bugTrackers: [
      {
        name: 'Bug Tracker C',
        type: 'JIRA',
        creation: new Date(0).getMilliseconds().toString(),
        ticketKey: 'Key-A',
        ticketTitle: 'Some Crazy Bug',
        ticketUrl: null
      }
    ],
    policies: {
      restrictedByOrg: true,
      invalidVersion: true,
      versionRestriction: 2,
      restrictedLicense: true
    },
    vulnerabilities: [
      {
        name: 'CVE-2012-1007',
        description:
          'Multiple cross-site scripting (XSS) vulnerabilities in Apache Struts 1.3.10 allow remote attackers to inject arbitrary web script or HTML via (1) the name parameter to struts-examples/upload/upload-submit.do, or the message parameter to (2) struts-cookbook/processSimple.do or (3) struts-cookbook/processDyna.do.',
        visible: true,
        references: [],
        severityCode: 'MEDIUM',
        severityValue: 4.3,
        confidentialityImpact: 'None',
        integrityImpact: 'Partial',
        availabilityImpact: 'None',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Medium',
        hasCve: true,
        hasCvss3: false
      },
      {
        name: 'CVE-2015-0899',
        description:
          'The MultiPageValidator implementation in Apache Struts 1 1.1 through 1.3.10 allows remote attackers to bypass intended access restrictions via a modified page parameter.',
        visible: true,
        references: [],
        severityCode: 'MEDIUM',
        severityValue: 5,
        confidentialityImpact: 'None',
        integrityImpact: 'Partial',
        availabilityImpact: 'None',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Low',
        hasCve: true,
        hasCvss3: false
      },
      {
        name: 'CVE-2016-1182',
        description:
          'ActionServlet.java in Apache Struts 1 1.x through 1.3.10 does not properly restrict the Validator configuration, which allows remote attackers to conduct cross-site scripting (XSS) attacks or cause a denial of service via crafted input, a related issue to CVE-2015-0899.',
        visible: true,
        references: [],
        severityCode: 'MEDIUM',
        severityValue: 6.4,
        confidentialityImpact: 'None',
        integrityImpact: 'Partial',
        availabilityImpact: 'Partial',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Low',
        hasCve: true,
        hasCvss3: false
      },
      {
        name: 'CVE-2016-1181',
        description:
          'ActionServlet.java in Apache Struts 1 1.x through 1.3.10 mishandles multithreaded access to an ActionForm instance, which allows remote attackers to execute arbitrary code or cause a denial of service (unexpected memory access) via a multipart request, a related issue to CVE-2015-0899.',
        visible: true,
        references: [],
        severityCode: 'MEDIUM',
        severityValue: 6.8,
        confidentialityImpact: 'Partial',
        integrityImpact: 'Partial',
        availabilityImpact: 'Partial',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Medium',
        hasCve: true,
        hasCvss3: false
      },
      {
        name: 'CVE-2014-0112',
        description:
          'ParametersInterceptor in Apache Struts before 2.3.16.2 does not properly restrict access to the getClass method, which allows remote attackers to "manipulate" the ClassLoader and execute arbitrary code via a crafted request.  NOTE: this vulnerability exists because of an incomplete fix for CVE-2014-0094.',
        visible: true,
        references: [],
        severityCode: 'HIGH',
        severityValue: 7.5,
        confidentialityImpact: 'Partial',
        integrityImpact: 'Partial',
        availabilityImpact: 'Partial',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Low',
        hasCve: true,
        hasCvss3: false
      },
      {
        name: 'CVE-2014-0114',
        description:
          'Apache Commons BeanUtils, as distributed in lib/commons-beanutils-1.8.0.jar in Apache Struts 1.x through 1.3.10 and in other products requiring commons-beanutils through 1.9.2, does not suppress the class property, which allows remote attackers to "manipulate" the ClassLoader and execute arbitrary code via the class parameter, as demonstrated by the passing of this parameter to the getClass method of the ActionForm object in Struts 1.',
        visible: true,
        references: [],
        severityCode: 'HIGH',
        severityValue: 7.5,
        confidentialityImpact: 'Partial',
        integrityImpact: 'Partial',
        availabilityImpact: 'Partial',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Low',
        hasCve: true,
        hasCvss3: false
      }
    ]
  },
  '1B6453892473A467D07372D45EB05ABC20316478': {
    name: 'OSS Enabled w/licenses',
    language: 'Java',
    grade: {
      value: 'A',
      score: 100
    },
    ossEnabled: true,
    licenses: ['Apache 2.0', 'MIT', 'LGPL', 'GPL', 'Apache 3.0']
  },
  '1B6453892473A467D07372D45EB05ABC20316479': {
    name: 'OSS Enabled w/o licenses',
    language: 'Java',
    grade: {
      value: 'A',
      score: 100
    },
    ossEnabled: true,
    licenses: []
  },
  e58bf1c660e6706d8e2cbb53bae110f574366102: {
    grade: {
      value: 'F',
      score: 0
    },
    tags: [
      'lots',
      'of',
      'tags',
      'here',
      'are',
      'some',
      'more',
      'tags',
      'for',
      'you',
      'to',
      'look',
      'at'
    ],
    version: '5.0.12.Final',
    name: 'hibernate-core-5.0.12.final.jar',
    language: 'Java',
    group: 'org.hibernate',
    vulnerabilities: [
      {
        name: 'CVE-2016-1181',
        description:
          'ActionServlet.java in Apache Struts 1 1.x through 1.3.10 mishandles multithreaded access to an ActionForm instance, which allows remote attackers to execute arbitrary code or cause a denial of service (unexpected memory access) via a multipart request, a related issue to CVE-2015-0899.',
        visible: true,
        references: [],
        severityCode: 'MEDIUM',
        severityValue: 6.8,
        confidentialityImpact: 'Partial',
        integrityImpact: 'Partial',
        availabilityImpact: 'Partial',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Medium',
        hasCve: true,
        hasCvss3: false
      },
      {
        name: 'Non-CVE Vulnerability',
        description:
          'This is an example of what a vulnerability will look like when there is no CVE.',
        visible: true,
        references: [],
        severityCode: 'LOW',
        severityValue: 5,
        confidentialityImpact: 'Partial',
        integrityImpact: 'None',
        availabilityImpact: 'None',
        authentication: 'None',
        accessVector: 'Network',
        accessComplexity: 'Low',
        hasCve: false,
        hasCvss3: false
      }
    ]
  },
  '1B6453892473A467D07372D45EB05ABC20316474': {
    name: 'No Version',
    language: 'Java',
    grade: {
      value: 'A',
      score: 100
    },
    version: ''
  },
  '1B6453892473A467D07372D45EB05ABC2031647A': {
    name: 'xxe-8.0.0.m21.jar',
    language: 'Java',
    grade: {
      value: 'A',
      score: 100
    },
    usage: {
      totalUsed: 100,
      total: 1000
    }
  },
  '1B6453892473A467D07372D45EB05ABC2031647B': {
    name: 'algo-httpserv',
    language: 'Node',
    grade: {
      value: 'B',
      score: 85
    },
    applications: [
      {
        id: '5',
        appId: '5',
        name: 'boopy doop',
        appLibraryStatus: 'NOT_A_PROBLEM'
      }
    ]
  },
  '1B6453892473A467D07372D45EB05ABC2031647X': {
    name: 'donotinstallthis',
    language: 'Node',
    grade: {
      value: 'B',
      score: 85
    }
  },
  '1B6453892473A467D07372D45EB05ABC2031647C': {
    name: 'WinForms',
    language: '.NET',
    grade: {
      value: 'C',
      score: 80
    }
  },
  '1B6453892473A467D07372D45EB05ABC2031647G': {
    name: '_json',
    language: 'Python',
    grade: {
      value: 'F',
      score: 65
    }
  },
  '1B6453892473A467D07372D45EB05ABC2031647D': {
    name: 'urllib2',
    language: 'Python',
    grade: {
      value: 'D',
      score: 70
    }
  },
  '1B6453892473A467D07372D45EB05ABC2031647E': {
    name: 'nokogiri',
    language: 'Ruby',
    grade: {
      value: 'F',
      score: 25
    }
  },
  '1B6453892473A467D07372D45EB05ABC2031647F': {
    name: 'bootstrap-sass',
    language: 'Ruby',
    grade: {
      value: 'F',
      score: 20
    }
  }
}

export const mockLibraries: Library[] = Object.keys(baseMockLibraries).map(
  (hash) => {
    const { language, ...library } = baseMockLibraries[hash]
    const mapped: Library = {
      id: buildLibraryCacheID({ hash, language }),
      hash,
      language,
      ossEnabled: false,
      usage: {
        total: 100,
        totalUsed: 10
      },
      versions: {
        current: {
          version: '1.0.0',
          releaseDate: new Date().getMilliseconds().toString()
        },
        latest: {
          version: '2.0.0',
          releaseDate: new Date().getMilliseconds().toString()
        }
      },
      policies: {
        restrictedByOrg: false,
        invalidVersion: false,
        versionRestriction: 0
      },
      licenses: [],
      tags: [],
      bugTrackers: [],
      libraryClassUsageCounts: [
        { appId: '1', usageCount: 5 },
        { appId: '2', usageCount: 4 }
      ],
      applications: [
        {
          id: '1',
          name: 'WebGoat',
          appId: '1',
          appLibraryStatus: 'REPORTED'
        },
        {
          id: '2',
          name: 'WebGoat 2',
          appId: '2',
          appLibraryStatus: 'REPORTED'
        }
      ],
      mergedApplications: [
        {
          id: '1',
          name: 'WebGoat',
          applicationID: '1',
          appLibraryStatus: 'REPORTED'
        },
        {
          id: '2',
          name: 'WebGoat 2',
          applicationID: '2',
          appLibraryStatus: 'REPORTED'
        }
      ],
      vulnerabilities: [],
      ...library
    }
    return mapped
  }
)
